import type {
  ContentProcessorState,
  ContentNode,
} from '../contentProcessor.types';

/**
 * Appends text to the content nodes, merging with the last text node if possible.
 * This is more efficient than creating many small text nodes.
 */
export function appendTextNode(
  nodes: ContentNode[],
  text: string,
): ContentNode[] {
  if (!text) return nodes;

  const lastNode = nodes[nodes.length - 1];
  if (lastNode?.type === 'text') {
    // Merge with existing text node
    lastNode.content += text;
    return nodes;
  }

  // Create new text node
  return [...nodes, { type: 'text', content: text }];
}

/**
 * Gets the current target for appending content based on tool-use nesting.
 * If we're inside a tool-use, returns its children array.
 * Otherwise, returns the root nodes array.
 */
export function getCurrentTarget(state: ContentProcessorState): ContentNode[] {
  const currentToolUse = state.toolUseStack[state.toolUseStack.length - 1];
  return currentToolUse == null ? state.nodes : currentToolUse.children;
}

/**
 * Appends text to the appropriate location based on tool-use nesting.
 */
export function appendText(
  state: ContentProcessorState,
  text: string,
): ContentProcessorState {
  if (!text) return state;

  const currentToolUse = state.toolUseStack[state.toolUseStack.length - 1];

  if (currentToolUse != null) {
    // Append to nested tool-use children
    currentToolUse.children = appendTextNode(currentToolUse.children, text);
    return state;
  }

  // Append to root nodes
  return {
    ...state,
    nodes: appendTextNode(state.nodes, text),
  };
}

/**
 * Appends text to the root-level nodes, bypassing tool-use nesting.
 * Use this for cancel messages that should always be visible
 * at the top level, even if a tool call is currently active.
 */
export function appendTextToRoot(
  state: ContentProcessorState,
  text: string,
): ContentProcessorState {
  if (!text) return state;

  return {
    ...state,
    nodes: appendTextNode(state.nodes, text),
  };
}
